<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import ExternalMessagesAPI from 'dashboard/api/externalMessages';
import TalkviaConnectedBankQueriesAPI from 'dashboard/api/talkviaConnectedBankQueries';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    InlineInput,
    NextInput,
    NextSelect,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    enableBankSimulation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      isEditingName: false,
      editName: '',
      labelCpf: 'CPF',
      labelRegistrationNumber: 'Matrícula',
      bankSimulationTitle: 'Simulação do banco',
      bankSimulationDescription:
        'Consulte os bancos conectados e, se houver retorno com sucesso, envie a mensagem para o contato.',
      bankSimulationCpfLabel: 'CPF para consulta',
      bankSimulationBankLabel: 'Banco',
      bankSimulationAllBanksLabel: 'Todos os bancos conectados',
      bankSimulationConsultLabel: 'Consultar bancos',
      bankSimulationCpfPlaceholder: '000.000.000-00',
      bankSimulationProcessedLabel: 'processados',
      bankSimulationWaitingResult: 'Aguardando retorno.',
      bankSimulationMarginLabel: 'Margem',
      bankSimulationMessageLabel: 'Mensagem para o contato',
      bankSimulationResultsLabel: 'Retorno da consulta',
      bankSimulationMissingCpf: 'Informe um CPF válido para consultar.',
      bankSimulationMissingProviders:
        'Nenhum banco conectado ativo para consulta.',
      bankSimulationMissingPhone:
        'Este contato precisa ter um telefone cadastrado para receber a mensagem.',
      bankSimulationMissingInbox:
        'Nenhuma caixa WhatsApp ou SMS disponível para enviar a mensagem.',
      bankSimulationNoSuccess:
        'A mensagem fica disponível quando ao menos um banco retornar sucesso.',
      bankSimulationCancelLabel: 'Cancelar',
      bankSimulationSendLabel: 'Enviar mensagem para o contato',
      showBankSimulationModal: false,
      bankSimulationCpf: '',
      bankSimulationProviders: [],
      selectedBankSimulationProvider: 'all',
      bankSimulationBatch: null,
      bankSimulationPollingTimer: null,
      isLoadingBankSimulationProviders: false,
      isConsultingBankSimulation: false,
      isSendingBankSimulation: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
      inboxes: 'inboxes/getInboxes',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
    contactCpf() {
      return this.contact.talkvia_profile?.cpf || '';
    },
    bankSimulationCpfDigits() {
      return String(this.bankSimulationCpf || '').replace(/\D/g, '');
    },
    bankSimulationMessage() {
      const contactName = this.contact.name || 'cliente';
      const successBank = this.successfulBankSimulationResults[0];
      const bankLabel = successBank?.provider_label || 'banco';
      return `Olá ${contactName}, sua simulação no ${bankLabel} foi aprovada com sucesso. Podemos seguir com o atendimento?`;
    },
    bankSimulationProviderOptions() {
      return [
        {
          id: 'all',
          label: this.bankSimulationAllBanksLabel,
        },
        ...this.bankSimulationProviders,
      ];
    },
    bankSimulationSelectOptions() {
      return this.bankSimulationProviderOptions.map(({ id, label }) => ({
        value: id,
        label,
      }));
    },
    bankSimulationResults() {
      return this.bankSimulationBatch?.results || [];
    },
    bankSimulationProcessedSummary() {
      if (!this.bankSimulationBatch) return '';
      return `${this.bankSimulationBatch.processed}/${this.bankSimulationBatch.total} ${this.bankSimulationProcessedLabel}`;
    },
    successfulBankSimulationResults() {
      return this.bankSimulationResults.filter(
        result => result.status === 'success'
      );
    },
    hasSuccessfulBankSimulation() {
      return this.successfulBankSimulationResults.length > 0;
    },
    hasActiveBankSimulation() {
      return ['awaiting', 'processing'].includes(
        this.bankSimulationBatch?.status
      );
    },
    canConsultBankSimulation() {
      return (
        this.bankSimulationCpfDigits.length === 11 &&
        this.bankSimulationProviders.length > 0 &&
        !this.isConsultingBankSimulation
      );
    },
    supportedBankSimulationInboxes() {
      return this.inboxes.filter(inbox => {
        const isWhatsApp = inbox.channel_type === INBOX_TYPES.WHATSAPP;
        const isSms = inbox.channel_type === INBOX_TYPES.SMS;
        const isTwilioSms =
          inbox.channel_type === INBOX_TYPES.TWILIO && inbox.medium === 'sms';

        return isWhatsApp || isSms || isTwilioSms;
      });
    },
    bankSimulationInbox() {
      const currentInbox = this.supportedBankSimulationInboxes.find(
        inbox => Number(inbox.id) === Number(this.currentChat?.inbox_id)
      );

      return currentInbox || this.supportedBankSimulationInboxes[0] || null;
    },
    canSendBankSimulation() {
      return (
        !!this.contact.phone_number &&
        !!this.bankSimulationInbox &&
        this.hasSuccessfulBankSimulation
      );
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    this.stopBankSimulationPolling();
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    async copyName() {
      try {
        await copyTextToClipboard(this.contact.name);
        useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      } catch {
        useAlert(this.$t('CONTACT_PANEL.COPY_FAILED') || 'Erro ao copiar');
      }
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async openBankSimulationModal() {
      if (!this.enableBankSimulation) return;

      this.showBankSimulationModal = true;
      this.bankSimulationCpf = this.contactCpf || this.bankSimulationCpf;
      this.bankSimulationBatch = null;
      await this.loadBankSimulationProviders();
    },
    closeBankSimulationModal() {
      if (this.isSendingBankSimulation || this.isConsultingBankSimulation)
        return;
      this.stopBankSimulationPolling();
      this.showBankSimulationModal = false;
    },
    async loadBankSimulationProviders() {
      if (this.bankSimulationProviders.length) return;

      this.isLoadingBankSimulationProviders = true;
      try {
        const { data } = await TalkviaConnectedBankQueriesAPI.providers();
        this.bankSimulationProviders = data.providers || [];
      } catch (error) {
        useAlert(
          error.response?.data?.error || 'Não foi possível carregar os bancos.'
        );
      } finally {
        this.isLoadingBankSimulationProviders = false;
      }
    },
    async consultBankSimulation() {
      if (!this.canConsultBankSimulation) return;

      this.isConsultingBankSimulation = true;
      this.bankSimulationBatch = null;
      try {
        const payload = { cpf: this.bankSimulationCpfDigits };
        if (this.selectedBankSimulationProvider !== 'all') {
          payload.provider = this.selectedBankSimulationProvider;
        }
        const { data } = await TalkviaConnectedBankQueriesAPI.create(payload);
        this.bankSimulationBatch = data;
        this.startBankSimulationPolling();
      } catch (error) {
        useAlert(
          error.response?.data?.error || 'Não foi possível iniciar a consulta.'
        );
      } finally {
        this.isConsultingBankSimulation = false;
      }
    },
    startBankSimulationPolling() {
      this.stopBankSimulationPolling();
      if (!this.hasActiveBankSimulation) return;

      this.bankSimulationPollingTimer = window.setInterval(
        this.refreshBankSimulationBatch,
        3000
      );
    },
    stopBankSimulationPolling() {
      if (!this.bankSimulationPollingTimer) return;

      window.clearInterval(this.bankSimulationPollingTimer);
      this.bankSimulationPollingTimer = null;
    },
    async refreshBankSimulationBatch() {
      if (!this.bankSimulationBatch?.id) return;

      try {
        const { data } = await TalkviaConnectedBankQueriesAPI.show(
          this.bankSimulationBatch.id
        );
        this.bankSimulationBatch = data;
        if (!this.hasActiveBankSimulation) this.stopBankSimulationPolling();
      } catch (error) {
        this.stopBankSimulationPolling();
      }
    },
    bankSimulationStatusLabel(status) {
      const labels = {
        awaiting: 'Aguardando',
        processing: 'Processando',
        completed: 'Concluído',
        partial_success: 'Concluído com erro',
        failed: 'Erro',
        timeout: 'Timeout',
        success: 'Sucesso',
        authorization_required: 'Autorização pendente',
        no_margin: 'Sem margem',
        error: 'Erro',
      };
      return labels[status] || status || '-';
    },
    bankSimulationStatusClass(status) {
      if (['success', 'completed'].includes(status)) {
        return 'bg-n-teal-3 text-n-teal-11';
      }
      if (['awaiting', 'processing'].includes(status)) {
        return 'bg-n-amber-3 text-n-amber-11';
      }
      return 'bg-n-ruby-3 text-n-ruby-11';
    },
    bankSimulationMarginSummary(result) {
      return `${this.bankSimulationMarginLabel}: ${result.margin}`;
    },
    async sendBankSimulationMessage() {
      if (!this.canSendBankSimulation) return;

      this.isSendingBankSimulation = true;
      try {
        await ExternalMessagesAPI.create({
          inbox_id: this.bankSimulationInbox.id,
          phone_number: this.contact.phone_number,
          name: this.contact.name,
          content: this.bankSimulationMessage,
          content_type: 'text',
          status: 'open',
        });
        useAlert('Simulação enviada com sucesso.');
        this.showBankSimulationModal = false;
      } catch (error) {
        const message =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Não foi possível enviar a simulação.';
        useAlert(message);
      } finally {
        this.isSendingBankSimulation = false;
      }
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div
    class="relative w-full border-b border-n-weak bg-n-solid-1 px-4 pb-4 pt-5"
  >
    <div
      class="grid w-full gap-x-3 gap-y-3 text-left rtl:text-right"
      :class="showAvatar ? 'grid-cols-[3rem_minmax(0,1fr)]' : 'grid-cols-1'"
    >
      <div v-if="showAvatar" class="pt-0.5">
        <Avatar
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
        />
      </div>

      <div class="flex min-w-0 w-full flex-col items-start gap-1.5">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <InlineInput
            v-if="isEditingName"
            ref="nameInput"
            v-model="editName"
            custom-input-class="!text-base !font-medium"
            class="!w-fit"
            @enter-press="saveNameEdit"
            @escape-press="cancelNameEdit"
            @blur="saveNameEdit"
          />
          <h3
            v-else
            class="group/name flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
            :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
            @click="startEditingName"
          >
            {{ contact.name }}
            <span
              class="i-lucide-pencil text-xs text-n-slate-10 opacity-0 group-hover/name:opacity-100 transition-opacity ml-1 align-middle"
            />
          </h3>
          <div class="flex flex-row items-center gap-2">
            <NextButton
              v-if="contact.name"
              v-tooltip.top="'Copiar nome'"
              ghost
              xs
              slate
              icon="i-lucide-clipboard"
              @click="copyName"
            />
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            editable
            @update="value => onFieldUpdate('phone_number', value)"
          />
          <ContactInfoRow
            v-if="contact.talkvia_profile && contact.talkvia_profile.cpf"
            :value="contact.talkvia_profile.cpf"
            icon="contact-card"
            emoji="🪪"
            :title="labelCpf"
            show-copy
          />
          <ContactInfoRow
            v-if="
              contact.talkvia_profile &&
              contact.talkvia_profile.registration_number
            "
            :value="contact.talkvia_profile.registration_number"
            icon="number-symbol"
            emoji="#️⃣"
            :title="labelRegistrationNumber"
            show-copy
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div
        class="col-span-full flex w-full items-center gap-1.5 rounded-xl border border-n-weak bg-n-alpha-1 p-1.5"
      >
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
        <NextButton
          v-if="enableBankSimulation"
          v-tooltip.top-end="'Simulação do banco'"
          icon="i-lucide-landmark"
          slate
          faded
          sm
          :disabled="!contact.phone_number"
          @click="openBankSimulationModal"
        />
        <NextButton
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="isAdmin"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
      </div>
      <EditContact
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <div
        v-if="showBankSimulationModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
        @click.self="closeBankSimulationModal"
        @keydown.esc="closeBankSimulationModal"
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="bank-simulation-title"
          class="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 shadow-2xl"
        >
          <header
            class="flex items-start justify-between gap-4 border-b border-n-weak px-5 py-4"
          >
            <div class="min-w-0">
              <h3
                id="bank-simulation-title"
                class="m-0 text-base font-semibold text-n-slate-12"
              >
                {{ bankSimulationTitle }}
              </h3>
              <p class="m-0 mt-1 text-sm text-n-slate-11">
                {{ bankSimulationDescription }}
              </p>
            </div>
            <NextButton
              v-tooltip.left="'Fechar'"
              icon="i-lucide-x"
              ghost
              slate
              sm
              :disabled="isSendingBankSimulation"
              @click="closeBankSimulationModal"
            />
          </header>

          <div class="flex min-h-0 flex-col gap-4 overflow-y-auto px-5 py-4">
            <div class="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <NextInput
                v-model="bankSimulationCpf"
                :label="bankSimulationCpfLabel"
                :placeholder="bankSimulationCpfPlaceholder"
              />
              <div class="flex flex-col gap-1">
                <label class="mb-0.5 text-heading-3 text-n-slate-12">
                  {{ bankSimulationBankLabel }}
                </label>
                <NextSelect
                  v-model="selectedBankSimulationProvider"
                  :options="bankSimulationSelectOptions"
                  :disabled="isLoadingBankSimulationProviders"
                  class="!w-full"
                />
              </div>
              <NextButton
                class="self-end"
                :label="bankSimulationConsultLabel"
                icon="i-lucide-search"
                blue
                :is-loading="isConsultingBankSimulation"
                :disabled="!canConsultBankSimulation"
                @click="consultBankSimulation"
              />
            </div>

            <p
              v-if="bankSimulationCpfDigits.length !== 11"
              class="m-0 text-sm text-n-ruby-11"
            >
              {{ bankSimulationMissingCpf }}
            </p>
            <p
              v-else-if="
                !bankSimulationProviders.length &&
                !isLoadingBankSimulationProviders
              "
              class="m-0 text-sm text-n-ruby-11"
            >
              {{ bankSimulationMissingProviders }}
            </p>

            <section
              v-if="bankSimulationBatch"
              class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p class="m-0 text-sm font-semibold text-n-slate-12">
                    {{ bankSimulationResultsLabel }}
                  </p>
                  <p class="m-0 mt-1 text-xs text-n-slate-10">
                    {{ bankSimulationProcessedSummary }}
                  </p>
                </div>
                <span
                  class="rounded-full px-2 py-1 text-xs font-medium"
                  :class="bankSimulationStatusClass(bankSimulationBatch.status)"
                >
                  {{ bankSimulationStatusLabel(bankSimulationBatch.status) }}
                </span>
              </div>

              <div class="mt-3 grid gap-2">
                <div
                  v-for="result in bankSimulationResults"
                  :key="result.id"
                  class="grid gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3 sm:grid-cols-[1fr_auto]"
                >
                  <div class="min-w-0">
                    <p
                      class="m-0 truncate text-sm font-semibold text-n-slate-12"
                    >
                      {{ result.provider_label }}
                    </p>
                    <p class="m-0 mt-1 text-xs text-n-slate-10">
                      {{
                        result.response_summary ||
                        result.error_message ||
                        bankSimulationWaitingResult
                      }}
                    </p>
                    <p
                      v-if="result.margin"
                      class="m-0 mt-1 text-xs text-n-slate-11"
                    >
                      {{ bankSimulationMarginSummary(result) }}
                    </p>
                  </div>
                  <span
                    class="h-fit rounded-full px-2 py-1 text-xs font-medium"
                    :class="bankSimulationStatusClass(result.status)"
                  >
                    {{ bankSimulationStatusLabel(result.status) }}
                  </span>
                </div>
              </div>
            </section>

            <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-3">
              <p class="m-0 text-xs font-medium uppercase text-n-slate-10">
                {{ bankSimulationMessageLabel }}
              </p>
              <p class="m-0 mt-2 whitespace-pre-wrap text-sm text-n-slate-12">
                {{ bankSimulationMessage }}
              </p>
            </div>
            <p v-if="!contact.phone_number" class="m-0 text-sm text-n-ruby-11">
              {{ bankSimulationMissingPhone }}
            </p>
            <p
              v-else-if="!bankSimulationInbox"
              class="m-0 text-sm text-n-ruby-11"
            >
              {{ bankSimulationMissingInbox }}
            </p>
            <p
              v-else-if="!hasSuccessfulBankSimulation"
              class="m-0 text-sm text-n-amber-11"
            >
              {{ bankSimulationNoSuccess }}
            </p>
          </div>

          <footer
            class="flex flex-wrap items-center justify-end gap-2 border-t border-n-weak px-5 py-4"
          >
            <NextButton
              :label="bankSimulationCancelLabel"
              slate
              faded
              :disabled="isSendingBankSimulation || isConsultingBankSimulation"
              @click="closeBankSimulationModal"
            />
            <NextButton
              :label="bankSimulationSendLabel"
              icon="i-lucide-send"
              blue
              :is-loading="isSendingBankSimulation"
              :disabled="!canSendBankSimulation"
              @click="sendBankSimulationMessage"
            />
          </footer>
        </section>
      </div>
    </div>
  </div>
</template>
